import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useAuth } from '@/contexts/AuthContext';
import { TasksAiEditor } from '@/ai-editor';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import {
  User,
  Clock,
  Paperclip,
  Plus,
  X,
  Check,
  Flag,
  Minus,
  ChevronUp,
  ChevronsUp,
  AlertTriangle,
  Repeat,
} from 'lucide-react';

export function CreateTaskSheet({ open, onOpenChange, onCreateTask, projectId: propProjectId, linkedComplianceRequirement, onClearLinkedRequirement }) {
  const { projectId: paramProjectId } = useParams();
  const projectId = propProjectId || paramProjectId; // Prefer prop, fallback to URL param
  const { userProfile } = useAuth();
  const [taskName, setTaskName] = useState('');
  const [description, setDescription] = useState('');
  const [subtasks, setSubtasks] = useState([]);
  const [newSubtaskText, setNewSubtaskText] = useState('');
  const [assignedTo, setAssignedTo] = useState([]);
  const [spaceMembers, setSpaceMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [dueDate, setDueDate] = useState(null);
  const [priority, setPriority] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [recurrence, setRecurrence] = useState('none');
  const [recurrenceInterval, setRecurrenceInterval] = useState(1);

  // Recurrence configuration
  const recurrenceOptions = [
    { value: 'none', label: 'Does not repeat' },
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'biweekly', label: 'Every 2 weeks' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'quarterly', label: 'Quarterly' },
    { value: 'yearly', label: 'Yearly' },
  ];

  // Priority configuration
  const priorityOptions = [
    { value: 'low', label: 'Low', icon: Minus, color: 'text-gray-500', bg: 'bg-gray-100' },
    { value: 'medium', label: 'Medium', icon: Flag, color: 'text-blue-500', bg: 'bg-blue-100' },
    { value: 'high', label: 'High', icon: ChevronUp, color: 'text-orange-500', bg: 'bg-orange-100' },
    { value: 'urgent', label: 'Urgent', icon: ChevronsUp, color: 'text-red-500', bg: 'bg-red-100' },
  ];

  // Fetch space members
  useEffect(() => {
    if (projectId && open) {
      fetchSpaceMembers();
    }
  }, [projectId, open]);

  // Set default assignee to current user
  useEffect(() => {
    if (userProfile?.id && assignedTo.length === 0) {
      setAssignedTo([userProfile.id]);
    }
  }, [userProfile, assignedTo.length]);

  // Pre-fill from linked compliance requirement
  useEffect(() => {
    if (linkedComplianceRequirement && open) {
      setTaskName(linkedComplianceRequirement.name || '');
      setDescription(linkedComplianceRequirement.description || '');
    }
  }, [linkedComplianceRequirement, open]);

  const fetchSpaceMembers = async () => {
    try {
      setLoadingMembers(true);
      const { data, error } = await supabase
        .from('space_members')
        .select(`
          user_id,
          user:user_profiles!user_id(id, first_name, last_name, avatar_url)
        `)
        .eq('project_id', projectId);

      if (error) throw error;

      const members = data.map(m => m.user);
      setSpaceMembers(members);
    } catch (error) {
      console.error('Error fetching space members:', error);
      toast.error('Failed to load team members');
    } finally {
      setLoadingMembers(false);
    }
  };

  const addSubtask = () => {
    if (newSubtaskText.trim()) {
      setSubtasks([...subtasks, { id: Date.now(), text: newSubtaskText.trim(), completed: false }]);
      setNewSubtaskText('');
    }
  };

  const removeSubtask = (id) => {
    setSubtasks(subtasks.filter(st => st.id !== id));
  };

  const updateSubtask = (id, text) => {
    setSubtasks(subtasks.map(st => st.id === id ? { ...st, text } : st));
  };

  const handleCreate = () => {
    const task = {
      name: taskName,
      description,
      subtasks,
      assignedTo,
      dueDate: dueDate ? dueDate.toISOString() : null,
      priority,
      attachments,
      recurrence_type: recurrence,
      recurrence_interval: recurrenceInterval,
      compliance_requirement_id: linkedComplianceRequirement?.id || null,
    };
    onCreateTask(task);
    // Reset form
    setTaskName('');
    setDescription('');
    setSubtasks([]);
    setNewSubtaskText('');
    setAssignedTo(userProfile?.id ? [userProfile.id] : []);
    setDueDate(null);
    setPriority(null);
    setAttachments([]);
    setRecurrence('none');
    setRecurrenceInterval(1);
    onClearLinkedRequirement?.();
    onOpenChange(false);
  };

  const handleToggleAssignee = (memberId) => {
    if (assignedTo.includes(memberId)) {
      setAssignedTo(assignedTo.filter(id => id !== memberId));
    } else {
      setAssignedTo([...assignedTo, memberId]);
    }
  };

  const handleAssignAll = () => {
    setAssignedTo(spaceMembers.map(m => m.id));
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // Validate file sizes (max 50MB each)
    for (const file of files) {
      if (file.size > 50 * 1024 * 1024) {
        toast.error(`File "${file.name}" is too large. Max size is 50MB.`);
        return;
      }
    }

    setUploadingAttachment(true);
    try {
      const uploadedFiles = await Promise.all(
        files.map(async (file) => {
          // Generate unique filename
          const fileExt = file.name.split('.').pop();
          const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;

          // Folder path: spaces/{projectId}/tasks/attachments
          const folderPath = `spaces/${projectId}/tasks/attachments`;

          // Get signed upload URL from edge function
          const { data: urlData, error: urlError } = await supabase.functions.invoke('generate-upload-url', {
            body: {
              action: 'upload',
              fileName: fileName,
              contentType: file.type || 'application/octet-stream',
              folderPath: folderPath,
            },
          });

          if (urlError) throw urlError;

          // Upload file to GCS using signed URL
          const uploadResponse = await fetch(urlData.signedUrl, {
            method: 'PUT',
            headers: {
              'Content-Type': file.type || 'application/octet-stream',
            },
            body: file,
          });

          if (!uploadResponse.ok) {
            throw new Error('Failed to upload file to storage');
          }

          return {
            name: file.name,
            url: urlData.publicUrl,
            size: file.size,
            type: file.type,
          };
        })
      );

      setAttachments([...attachments, ...uploadedFiles]);
      toast.success(`${uploadedFiles.length} file(s) attached`);
    } catch (error) {
      console.error('Error uploading attachments:', error);
      toast.error('Failed to upload attachments: ' + error.message);
    } finally {
      setUploadingAttachment(false);
    }
  };

  const removeAttachment = (index) => {
    setAttachments(attachments.filter((_, i) => i !== index));
  };

  const assignedMembers = spaceMembers.filter(m => assignedTo.includes(m.id));

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-4xl overflow-y-auto p-0 flex flex-col">
        <SheetHeader className="px-6 pt-6 pb-4 border-b">
          <SheetTitle className="text-sm font-medium tracking-wider text-muted-foreground">
            TASK
          </SheetTitle>
        </SheetHeader>

        {/* Linked Compliance Requirement Banner */}
        {linkedComplianceRequirement && (
          <div className="mx-6 mt-4 p-3 rounded-lg bg-amber-50 border border-amber-200 flex items-center gap-3">
            <div className="h-8 w-8 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="h-4 w-4" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-amber-800">Linked to Compliance Checklist</p>
              <p className="text-xs text-amber-600 truncate">{linkedComplianceRequirement.name}</p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-amber-600 hover:text-amber-800 hover:bg-amber-100"
              onClick={() => onClearLinkedRequirement?.()}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        )}

        <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
          {/* Quick Actions */}
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon" className="h-9 w-9">
                  <User className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-64">
                <DropdownMenuLabel>Assign to</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {loadingMembers ? (
                  <DropdownMenuItem disabled>Loading members...</DropdownMenuItem>
                ) : spaceMembers.length === 0 ? (
                  <DropdownMenuItem disabled>No team members found</DropdownMenuItem>
                ) : (
                  <>
                    <DropdownMenuItem
                      onClick={handleAssignAll}
                      className="flex items-center gap-2 font-medium"
                    >
                      <Checkbox
                        checked={assignedTo.length === spaceMembers.length}
                        className="pointer-events-none"
                      />
                      <span>All space members</span>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    {spaceMembers.map((member) => (
                      <DropdownMenuItem
                        key={member.id}
                        onClick={() => handleToggleAssignee(member.id)}
                        className="flex items-center gap-2"
                      >
                        <Checkbox
                          checked={assignedTo.includes(member.id)}
                          className="pointer-events-none"
                        />
                        <Avatar className="h-5 w-5">
                          <AvatarImage src={member.avatar_url} />
                          <AvatarFallback className="text-xs">
                            {member.first_name?.[0] || 'U'}
                          </AvatarFallback>
                        </Avatar>
                        <span>{member.first_name} {member.last_name}</span>
                      </DropdownMenuItem>
                    ))}
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
            {assignedMembers.length > 0 && (
              <div className="flex -space-x-2">
                {assignedMembers.slice(0, 3).map((member) => (
                  <Avatar key={member.id} className="h-9 w-9 border-2 border-background">
                    <AvatarImage src={member.avatar_url} />
                    <AvatarFallback className="text-xs">
                      {member.first_name?.[0] || 'U'}
                    </AvatarFallback>
                  </Avatar>
                ))}
                {assignedMembers.length > 3 && (
                  <div className="h-9 w-9 rounded-full border-2 border-background bg-muted flex items-center justify-center text-xs font-medium">
                    +{assignedMembers.length - 3}
                  </div>
                )}
              </div>
            )}
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant={dueDate ? "default" : "outline"}
                  size={dueDate ? "sm" : "icon"}
                  className={dueDate ? "gap-2 h-9" : "h-9 w-9"}
                >
                  <Clock className="h-4 w-4" />
                  {dueDate && (
                    <span>
                      {dueDate.toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                      })}
                    </span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={dueDate}
                  onSelect={setDueDate}
                  initialFocus
                />
                {dueDate && (
                  <div className="p-3 border-t">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-full"
                      onClick={() => setDueDate(null)}
                    >
                      Clear date
                    </Button>
                  </div>
                )}
              </PopoverContent>
            </Popover>

            {/* Priority Selector */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                {priority ? (
                  <Button
                    variant="outline"
                    size="sm"
                    className={`gap-2 h-9 ${priorityOptions.find(p => p.value === priority)?.color}`}
                  >
                    {(() => {
                      const opt = priorityOptions.find(p => p.value === priority);
                      const Icon = opt?.icon || Flag;
                      return <Icon className="h-4 w-4" />;
                    })()}
                    {priorityOptions.find(p => p.value === priority)?.label}
                  </Button>
                ) : (
                  <Button variant="outline" size="icon" className="h-9 w-9">
                    <Flag className="h-4 w-4" />
                  </Button>
                )}
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                <DropdownMenuLabel>Priority</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {priorityOptions.map((opt) => {
                  const Icon = opt.icon;
                  return (
                    <DropdownMenuItem
                      key={opt.value}
                      onClick={() => setPriority(opt.value)}
                      className="flex items-center gap-2"
                    >
                      <Icon className={`h-4 w-4 ${opt.color}`} />
                      <span>{opt.label}</span>
                      {priority === opt.value && (
                        <Check className="h-4 w-4 ml-auto" />
                      )}
                    </DropdownMenuItem>
                  );
                })}
                {priority && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => setPriority(null)}
                      className="text-muted-foreground"
                    >
                      Clear priority
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Recurrence Selector */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                {recurrence !== 'none' ? (
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-2 h-9 text-purple-600"
                  >
                    <Repeat className="h-4 w-4" />
                    {recurrenceOptions.find(r => r.value === recurrence)?.label}
                  </Button>
                ) : (
                  <Button variant="outline" size="icon" className="h-9 w-9">
                    <Repeat className="h-4 w-4" />
                  </Button>
                )}
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                <DropdownMenuLabel>Repeat</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {recurrenceOptions.map((opt) => (
                  <DropdownMenuItem
                    key={opt.value}
                    onClick={() => setRecurrence(opt.value)}
                    className="flex items-center gap-2"
                  >
                    <span>{opt.label}</span>
                    {recurrence === opt.value && (
                      <Check className="h-4 w-4 ml-auto" />
                    )}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="task-name" className="text-sm font-medium">
              Name
            </Label>
            <Input
              id="task-name"
              placeholder="Task name"
              value={taskName}
              onChange={(e) => setTaskName(e.target.value)}
              className="h-11 px-4 text-base focus-visible:ring-2"
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Description</Label>
            <TasksAiEditor
              value={description}
              onChange={setDescription}
              placeholder="What is this task about?"
            />
          </div>

          {/* Subtasks */}
          <div className="space-y-3">
            {subtasks.length > 0 && (
              <div className="space-y-2">
                <Label className="text-xs font-medium tracking-wider text-muted-foreground">
                  SUBTASKS
                </Label>
                <div className="space-y-2">
                  {subtasks.map((subtask, index) => (
                    <div key={subtask.id} className="flex items-center gap-3">
                      <span className="text-sm text-muted-foreground w-6">
                        {index + 1}
                      </span>
                      <Checkbox disabled />
                      <Input
                        value={subtask.text}
                        onChange={(e) => updateSubtask(subtask.id, e.target.value)}
                        className="flex-1 h-9"
                        placeholder="Subtask description"
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-9 w-9"
                        onClick={() => removeSubtask(subtask.id)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="flex gap-2">
              <Input
                value={newSubtaskText}
                onChange={(e) => setNewSubtaskText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addSubtask();
                  }
                }}
                placeholder="Add a subtask"
                className="flex-1 h-9"
              />
              <Button
                variant="outline"
                size="sm"
                className="gap-2 h-9"
                onClick={addSubtask}
                disabled={!newSubtaskText.trim()}
              >
                <Plus className="h-4 w-4" />
                Add
              </Button>
            </div>
          </div>

          {/* Attachments */}
          <div className="space-y-3">
            {attachments.length > 0 && (
              <div className="space-y-2">
                <Label className="text-xs font-medium tracking-wider text-muted-foreground">
                  ATTACHMENTS
                </Label>
                <div className="space-y-2">
                  {attachments.map((file, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-3 p-3 border rounded-lg bg-muted/50"
                    >
                      <Paperclip className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{file.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {(file.size / 1024).toFixed(1)} KB
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 flex-shrink-0"
                        onClick={() => removeAttachment(index)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div>
              <input
                type="file"
                id="attachment-upload"
                multiple
                onChange={handleFileSelect}
                className="hidden"
                disabled={uploadingAttachment}
              />
              <label htmlFor="attachment-upload">
                <Button
                  variant="ghost"
                  size="sm"
                  className="gap-2 text-muted-foreground hover:text-foreground h-9 -ml-2"
                  asChild
                  disabled={uploadingAttachment}
                >
                  <span>
                    <Paperclip className="h-4 w-4" />
                    {uploadingAttachment ? 'Uploading...' : 'Add attachment'}
                  </span>
                </Button>
              </label>
            </div>
          </div>
        </div>

        {/* Actions - Fixed at bottom */}
        <div className="border-t px-6 py-4 mt-auto bg-background">
          <div className="flex items-center justify-end gap-3">
            <Button
              variant="ghost"
              onClick={() => onOpenChange(false)}
              className="h-10 px-4"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreate}
              disabled={!taskName.trim()}
              className="h-10 px-6"
            >
              Create
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
